import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    FiAlertCircle,
    FiArrowLeft,
    FiArrowRight,
    FiCheck,
    FiEye,
    FiEyeOff,
    FiKey,
    FiLock,
    FiMail,
    FiShield,
} from "react-icons/fi";
import { RoutesPaths } from "../Router/constant";
import { toast } from "react-toastify";
import { verifyPartnerEmail, resetPartnerPassword } from "../Services/authService";

const FEATURES = [
    { Icon: FiMail, title: "Verify your email", text: "Confirm the email linked to your vendor account." },
    { Icon: FiKey, title: "Choose a new password", text: "Enter your current password and a new one." },
    { Icon: FiShield, title: "Sign in securely", text: "Use your new password from the next sign in." },
];

export default function ResetPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [verifiedEmail, setVerifiedEmail] = useState("");
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleVerifyEmail = async () => {
        setError("");
        if (!email) return setError("Please enter email.");

        setLoading(true);
        try {
            const res = await verifyPartnerEmail({ email });
            if (res.data.status === true) {
                setVerifiedEmail(res.data.data.email);
                toast.success(res.data.data.message);
            } else {
                setError(res.data.data.message || "Email verification failed");
            }
        } catch (err) {
            setError(
                err?.response?.data?.data?.message ||
                    err?.data?.data?.message ||
                    "Email verification failed",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        setError("");
        if (!oldPassword || !newPassword || !confirmPassword) {
            return setError("Please fill all password fields.");
        }
        if (newPassword !== confirmPassword) {
            return setError("New password and confirm password do not match.");
        }

        setLoading(true);
        try {
            const res = await resetPartnerPassword({
                email: verifiedEmail,
                oldPassword,
                newPassword,
                confirmPassword,
            });
            if (res.data.status === true) {
                toast.success(res.data.data.message);
                navigate(RoutesPaths.SIGNIN);
            } else {
                setError(res.data.data.message || "Password reset failed");
            }
        } catch (err) {
            setError(err?.response?.data?.data?.message || "Password reset failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (loading) return;
        if (verifiedEmail) handleResetPassword();
        else handleVerifyEmail();
    };

    const changeEmail = () => {
        setVerifiedEmail("");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setError("");
    };

    const clearError = () => error && setError("");

    const passwordsMismatch = Boolean(confirmPassword) && newPassword !== confirmPassword;

    const renderPasswordField = ({ id, label, value, onChange, show, setShow, placeholder, autoComplete }) => (
        <>
            <label className="qsi-label" htmlFor={id}>
                {label}
            </label>
            <div className="qsi-input">
                <FiLock aria-hidden="true" />
                <input
                    id={id}
                    type={show ? "text" : "password"}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        clearError();
                    }}
                />
                <button
                    type="button"
                    className="qsi-eye"
                    onClick={() => setShow(!show)}
                    aria-label={show ? "Hide password" : "Show password"}
                >
                    {show ? <FiEyeOff /> : <FiEye />}
                </button>
            </div>
        </>
    );

    const step = verifiedEmail ? 2 : 1;

    return (
        <div className="qsi-page qsi-partner">
            {/* Brand panel */}
            <aside className="qsi-panel qsi-panel-partner">
                <img src="/assets/img/quimeraLogoPartner.svg" alt="Quimera Logistics" className="qsi-panel-logo" />

                <div className="qsi-panel-copy">
                    <span className="qsi-eyebrow">Vendor portal</span>
                    <h1>Reset your password</h1>
                    <p>Two quick steps and you&apos;re back to managing bookings, contracts and services.</p>
                    <ul className="qsi-features">
                        {FEATURES.map(({ Icon, title, text }) => (
                            <li key={title}>
                                <span className="qsi-feature-icon" aria-hidden="true">
                                    <Icon />
                                </span>
                                <span>
                                    <strong>{title}</strong>
                                    <small>{text}</small>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="qsi-panel-art" aria-hidden="true">
                    <img src={`${process.env.PUBLIC_URL}/assets/img/GlobalLogisticspartner.png`} alt="" />
                </div>
            </aside>

            {/* Form */}
            <main className="qsi-main">
                <img src="/assets/img/quimeraLogo.svg" alt="Quimera Logistics" className="qsi-mobile-logo" />

                <form className="qsi-card" onSubmit={handleSubmit} noValidate>
                    <span className="qrp-key" aria-hidden="true">
                        <FiKey />
                    </span>

                    <h2 className="qsi-title">Reset password</h2>
                    <p className="qsi-subtitle">
                        {verifiedEmail
                            ? "Enter your old password and choose a new password."
                            : "Enter your email to verify your account."}
                    </p>

                    {/* Step indicator */}
                    <ol className="qrp-steps" aria-label="Reset steps">
                        <li className={step === 1 ? "active" : "done"}>
                            <span>{step > 1 ? <FiCheck /> : 1}</span> Verify email
                        </li>
                        <li className="qrp-step-line" aria-hidden="true" />
                        <li className={step === 2 ? "active" : ""}>
                            <span>2</span> New password
                        </li>
                    </ol>

                    {error && (
                        <div className="qsi-error" role="alert">
                            <FiAlertCircle aria-hidden="true" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Step 1: Email */}
                    {!verifiedEmail && (
                        <>
                            <label className="qsi-label" htmlFor="qrp-email">
                                Email
                            </label>
                            <div className="qsi-input">
                                <FiMail aria-hidden="true" />
                                <input
                                    id="qrp-email"
                                    type="email"
                                    autoComplete="username"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        clearError();
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {/* Step 2: Passwords */}
                    {verifiedEmail && (
                        <>
                            <div className="qrp-verified">
                                <FiCheck aria-hidden="true" />
                                <span>
                                    Verified: <strong>{verifiedEmail}</strong>
                                </span>
                                <button type="button" onClick={changeEmail} disabled={loading}>
                                    Change
                                </button>
                            </div>

                            {renderPasswordField({
                                id: "qrp-old",
                                label: "Old password",
                                value: oldPassword,
                                onChange: setOldPassword,
                                show: showOldPassword,
                                setShow: setShowOldPassword,
                                placeholder: "Enter your old password",
                                autoComplete: "current-password",
                            })}

                            {renderPasswordField({
                                id: "qrp-new",
                                label: "New password",
                                value: newPassword,
                                onChange: setNewPassword,
                                show: showNewPassword,
                                setShow: setShowNewPassword,
                                placeholder: "Enter your new password",
                                autoComplete: "new-password",
                            })}

                            {renderPasswordField({
                                id: "qrp-confirm",
                                label: "Confirm password",
                                value: confirmPassword,
                                onChange: setConfirmPassword,
                                show: showConfirmPassword,
                                setShow: setShowConfirmPassword,
                                placeholder: "Confirm your password",
                                autoComplete: "new-password",
                            })}

                            {passwordsMismatch && (
                                <p className="qrp-hint">Passwords do not match yet.</p>
                            )}
                        </>
                    )}

                    <button type="submit" className="qsi-submit" disabled={loading} aria-busy={loading}>
                        {loading ? (
                            <>
                                <span className="qo-spinner" aria-hidden="true" /> Processing...
                            </>
                        ) : (
                            <>
                                {verifiedEmail ? "Reset password" : "Verify email"}{" "}
                                <FiArrowRight aria-hidden="true" />
                            </>
                        )}
                    </button>

                    <NavLink to={RoutesPaths.SIGNIN} className="qrp-back">
                        <FiArrowLeft aria-hidden="true" /> Back to log in
                    </NavLink>
                </form>

                <p className="qsi-footer">© Quimera {new Date().getFullYear()}. All rights reserved.</p>
            </main>
        </div>
    );
}
